// Shrinks and crops a photo in the browser before upload: at most 1200 px wide, JPEG,
// cut to the shape the email slot needs (center crop).

export interface Prepared {
  blob: Blob;
  width: number;
  height: number;
}

const MAX_W = 1200;

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      /* fall back */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export async function preparePhoto(file: File, aspect: number | null): Promise<Prepared> {
  const src = await decode(file);
  const sw = 'naturalWidth' in src ? src.naturalWidth : src.width;
  const sh = 'naturalHeight' in src ? src.naturalHeight : src.height;
  if (!sw || !sh) throw new Error('That file is not a photo the browser can open. Try a JPG or PNG.');
  // Crop box in source pixels.
  let cw = sw;
  let ch = sh;
  if (aspect) {
    if (sw / sh > aspect) cw = Math.round(sh * aspect);
    else ch = Math.round(sw / aspect);
  }
  const cx = Math.round((sw - cw) / 2);
  // Faces are usually in the upper part of a portrait photo, so crop a bit higher than center.
  const cy = Math.round(Math.max(0, (sh - ch) * 0.35));
  const outW = Math.min(MAX_W, cw);
  const outH = Math.round((outW / cw) * ch);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src as CanvasImageSource, cx, cy, cw, ch, 0, 0, outW, outH);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.84));
  if (!blob) throw new Error('Could not prepare that photo. Try another one.');
  return { blob, width: outW, height: outH };
}
